import {
  type ComputedRef,
  type InjectionKey,
  type Ref,
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
} from 'vue'
import { useRoute } from 'vue-router'
import { useGlobalShortcut } from '../../../composables/useKeyboardShortcut'
import type { KeyboardShortcut } from '../../../lib/keyboard-shortcut'
import { findActiveNavLink } from './nav-items'

const SIDEBAR_COOKIE_NAME = 'sidebar_state'
/** Cookie holding the comma-separated ids of the nav groups the user closed. */
const SIDEBAR_GROUPS_COOKIE_NAME = 'sidebar_groups_closed'
/** Cookie holding the user's preferred nav width in px. */
const SIDEBAR_WIDTH_COOKIE_NAME = 'sidebar_width'
const SIDEBAR_DEFAULT_WIDTH = 240
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
export const SIDEBAR_KEYBOARD_SHORTCUT: KeyboardShortcut = { key: 'b' }
/** Viewport at/below which the sidebar becomes an overlay sheet. */
const SIDEBAR_MOBILE_BREAKPOINT = 768

/** The desktop nav: full width with labels, or a narrow rail of icons. */
export type SidebarState = 'expanded' | 'rail'

export interface SidebarContext {
  openMobile: Ref<boolean>
  isMobile: Ref<boolean>
  /** What the nav shows now: the user's choice on desktop, always `expanded` on a phone or in setup mode. */
  state: ComputedRef<SidebarState>
  /** The user's preferred nav width in px (persisted to a cookie). */
  width: Ref<number>
  /** Set the nav width in px, applying it as a live override and persisting it unless `persist` is false. */
  setWidth: (px: number, persist?: boolean) => void
  /** Id of the nav row lit for the current route, or `''` when no place claims it. */
  activeId: ComputedRef<string>
  /** Sets the desktop state the user prefers and persists it to a cookie. */
  setState: (value: SidebarState) => void
  setOpenMobile: (value: boolean) => void
  toggle: () => void
  /** Whether the nav group with this id shows its rows (persisted to a cookie). */
  isGroupOpen: (id: string) => boolean
  setGroupOpen: (id: string, value: boolean) => void
}

const SIDEBAR_KEY: InjectionKey<SidebarContext> = Symbol('AppSidebar')

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1]
}

function readStoredWidth(): number | undefined {
  const cookie = readCookie(SIDEBAR_WIDTH_COOKIE_NAME)
  if (cookie === undefined) return undefined
  const parsed = Number.parseFloat(cookie)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

export interface ProvideSidebarOptions {
  /** Initial desktop state when no cookie is present. Defaults to `expanded`. */
  defaultState?: SidebarState
  /** Ids of the nav groups that start closed when no cookie is present. Defaults to none. */
  defaultClosedGroups?: readonly string[]
  /** Keeps the panel at full width whatever the stored preference says. A getter; never writes the cookie. */
  forceExpanded?: () => boolean
}

export function provideSidebar(options: ProvideSidebarOptions = {}): SidebarContext {
  const cookie = readCookie(SIDEBAR_COOKIE_NAME)
  const stored = cookie === 'expanded' || cookie === 'rail' ? cookie : undefined
  const preferred = ref<SidebarState>(stored ?? options.defaultState ?? 'expanded')
  const openMobile = ref(false)
  const isMobile = ref(
    typeof window !== 'undefined' &&
      window.matchMedia(`(max-width: ${SIDEBAR_MOBILE_BREAKPOINT - 1}px)`).matches,
  )
  const route = useRoute()
  const activeId = computed(() => findActiveNavLink(route.path)?.id ?? '')

  const forceExpanded = options.forceExpanded ?? (() => false)
  const state = computed<SidebarState>(() =>
    forceExpanded() || isMobile.value ? 'expanded' : preferred.value,
  )

  const storedWidth = readStoredWidth()
  const width = ref(storedWidth ?? SIDEBAR_DEFAULT_WIDTH)

  function setWidth(px: number, persist = true) {
    width.value = px
    if (typeof document === 'undefined') return
    document.documentElement.style.setProperty('--sidebar-width', `${px}px`)
    if (persist) {
      document.cookie = `${SIDEBAR_WIDTH_COOKIE_NAME}=${px}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    }
  }

  function setState(value: SidebarState) {
    preferred.value = value
    if (typeof document !== 'undefined') {
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${value}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    }
  }
  function setOpenMobile(value: boolean) {
    openMobile.value = value
  }
  function toggle() {
    if (isMobile.value) setOpenMobile(!openMobile.value)
    else setState(preferred.value === 'expanded' ? 'rail' : 'expanded')
  }

  const groupsCookie = readCookie(SIDEBAR_GROUPS_COOKIE_NAME)
  const closedGroups = ref<ReadonlySet<string>>(
    new Set(
      groupsCookie === undefined
        ? options.defaultClosedGroups
        : groupsCookie.split(',').filter(Boolean),
    ),
  )

  function isGroupOpen(id: string): boolean {
    return !closedGroups.value.has(id)
  }
  function setGroupOpen(id: string, value: boolean) {
    const next = new Set(closedGroups.value)
    if (value) next.delete(id)
    else next.add(id)
    closedGroups.value = next
    if (typeof document !== 'undefined') {
      document.cookie = `${SIDEBAR_GROUPS_COOKIE_NAME}=${[...next].join(',')}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    }
  }

  let mql: MediaQueryList | undefined
  function onMediaChange(event: MediaQueryListEvent) {
    isMobile.value = event.matches
    if (!event.matches) openMobile.value = false
  }

  useGlobalShortcut(SIDEBAR_KEYBOARD_SHORTCUT, toggle)

  onMounted(() => {
    if (storedWidth !== undefined && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--sidebar-width', `${storedWidth}px`)
    }
    if (typeof window === 'undefined') return
    mql = window.matchMedia(`(max-width: ${SIDEBAR_MOBILE_BREAKPOINT - 1}px)`)
    isMobile.value = mql.matches
    mql.addEventListener('change', onMediaChange)
  })
  onBeforeUnmount(() => {
    mql?.removeEventListener('change', onMediaChange)
    if (typeof document !== 'undefined')
      document.documentElement.style.removeProperty('--sidebar-width')
  })

  const context: SidebarContext = {
    openMobile,
    isMobile,
    state,
    width,
    setWidth,
    activeId,
    setState,
    setOpenMobile,
    toggle,
    isGroupOpen,
    setGroupOpen,
  }
  provide(SIDEBAR_KEY, context)
  return context
}

export function useSidebar(): SidebarContext {
  const context = inject(SIDEBAR_KEY)
  if (!context) {
    throw new Error('useSidebar() must be used within an AppSidebarProvider / AppShell')
  }
  return context
}
